import { Button } from '@pmi/dsm-react';
import { ArrowBackIcon } from '@pmi/dsm-react/dist/components/icons/24';
import { useNavigate, useParams } from 'react-router';
import { useGetOrderHistoryListQuery } from './apiSlice';
import { OrderHistoryResponse } from '../../shared/models/OrderHistory';
import { OrderConfirmationData } from '../../shared/components/Cart/models';
import OrderConfirmationView from '../../shared/components/Cart/OrderConfirmationView';
import { useGetAccountDataQuery } from '../../shared/navigation/apiSlice';
import useBreakpoint, {
  SIZE_SM,
  SIZE_XS,
} from '../../shared/hooks/useBreakpoint';

const mapOrderHistoryToConfirmation = (
  order: OrderHistoryResponse,
  accountName?: string,
): OrderConfirmationData => {
  const { billingAddress } = order;

  return {
    accountName,
    order: {
      incrementId: order.orderNumber,
      orderDate: order.orderDate,
      currencyCode: order.currencyCode || 'USD',
      subtotal: order.subtotal,
      taxTotal: order.orderTaxes,
      grandTotal: order.grandTotal,
      discountTotal: order.orderDiscount,
    },
    items: order.orderItems.map((item) => ({
      id: item.sku,
      name: item.title,
      subtotal: item.subTotal,
    })),
    user: {
      firstName: billingAddress?.firstName ?? '',
      lastName: billingAddress?.lastName ?? '',
      email: order.email,
    },
    payment: {
      ccType: order.creditCardType,
      ccLast4: order.creditCardLastFourDigit,
      method: order.paymentMethod,
    },
    billingAddress: billingAddress
      ? {
          firstName: billingAddress.firstName,
          lastName: billingAddress.lastName,
          address1: billingAddress.address1,
          address2: billingAddress.address2,
          city: billingAddress.city,
          state: billingAddress.state,
          postalCode: billingAddress.postalCode,
          country: billingAddress.country || billingAddress.countryCode,
        }
      : undefined,
  };
};

export default function AdminOrderConfirmation() {
  const navigate = useNavigate();
  const { accountId, orderId } = useParams();
  const { data, isLoading, isFetching } = useGetOrderHistoryListQuery(
    { accountId },
    { skip: !accountId },
  );
  const { data: accountData } = useGetAccountDataQuery(accountId, {
    skip: !accountId,
  });
  const bp = useBreakpoint();
  const isMobile = bp === SIZE_SM || bp === SIZE_XS;

  if (isLoading || isFetching) {
    return <div>Loading...</div>;
  }

  const order = data?.find((o) => String(o.orderId) === orderId);

  if (!order) {
    return <div className="text-danger">Order not found.</div>;
  }

  return (
    <>
      <div>
        <Button
          className="print-hidden"
          fullWidth={isMobile}
          icon={ArrowBackIcon}
          iconAlign="left"
          titleText="Go Back"
          enforceTitleCase={false}
          size="sm"
          variant="outline-secondary"
          onClick={() => navigate(-1)}
        />
      </div>

      <OrderConfirmationView
        accountId={accountId}
        data={mapOrderHistoryToConfirmation(order, accountData?.name)}
        onGoToOrders={() => navigate(`/admin/${accountId}/order-history`)}
        onReturnToProducts={() =>
          navigate(`/admin/${accountId}/${order.agreementNumber}`)
        }
      />
    </>
  );
}
